import {useState} from 'react';
import {useSelector} from 'react-redux';

import {isNil} from 'ramda';
import {compact} from 'ramda-adjunct';

import {carAuditApi, useMoveInspectionDataMutation} from '@dms/api/core';
import {vehicleApi} from '@dms/api/sales';
import {handleApiError} from '@dms/shared';

import {useThunkDispatch} from '../../../hooks/useThunkDispatch';
import {addAssets, deleteAssets, updateAuditFields} from '../../../store/carAudit/reducer';
import {
  selectActiveAuditId,
  selectAudit,
  selectAuditAssets,
  selectStructureCategories,
} from '../../../store/carAudit/selectors';
import {AuditCategoryOfStructure} from '../../../types/AuditCategoryOfStructure';
import {AuditDataAssetsFilesBody} from '../../../types/AuditDataAssetsFilesBody';
import {AuditDamageValueBody, TConditionForm} from '../../../types/ConditionTypes';
import {useFormRenderer} from '../../FinalForm/hooks/useFormRenderer';
import {AuditParamType} from '../types/AuditParamType';
import {buildMoveAuditDamagePayload} from '../utils/buildMoveAuditDamagePayload';
import {collectDamageAssets} from '../utils/collectDamageAssets';
import {getAuditFieldData} from '../utils/getAuditFieldData';
import {getDamageMoveTargetCategories} from '../utils/getDamageMoveTargetCategories';
import {getFormFieldName} from '../utils/getFormFieldName';
import {getParamFromCategory} from '../utils/getParamFromCategory';
import {isDamageValueArray} from '../utils/isDamageValueArray';

type UseMoveDamageParams = {
  /** The part the damage currently lives under. */
  category: AuditCategoryOfStructure;
  isEdit: boolean;
  damageId: string;
  damageType: string | null;
  damageComment: string | null;
  vehicleId: string | undefined;
  assetsForSave: Record<string, AuditDataAssetsFilesBody[]>;
  assetsForDelete: Record<string, string[]>;
  deleteAssetsForParam: (paramDefinitionId: string, imageIds?: string[]) => Promise<void>;
  deleteAssetsOfVehicleCreate: (imageIds?: string[]) => void;
  lastOrderNumber: number;
  onClose: () => void;
};

type UseMoveDamageResult = {
  /** Damage-capable parts the current damage can move to, including the current part itself. */
  targetCategories: AuditCategoryOfStructure[];
  canChangePart: boolean;
  setTargetCategoryId: (categoryId: string) => void;
  /** The picked target, clamped back to the current part if it is not among `targetCategories`. */
  resolvedTargetCategory: AuditCategoryOfStructure;
  isPartChanged: boolean;
  moveDamage: (targetCategory: AuditCategoryOfStructure) => Promise<void>;
};

/**
 * Encapsulates moving a damage to a different part: which parts it can move to, the currently
 * picked target, and the move itself (the `moveInspectionData` request, the resulting local
 * Final Form / redux sync, and cache invalidation).
 */
export const useMoveDamage = ({
  category,
  isEdit,
  damageId,
  damageType,
  damageComment,
  vehicleId,
  assetsForSave,
  assetsForDelete,
  deleteAssetsForParam,
  deleteAssetsOfVehicleCreate,
  lastOrderNumber,
  onClose,
}: UseMoveDamageParams): UseMoveDamageResult => {
  const dispatch = useThunkDispatch();
  const {
    form: {
      getFieldState,
      mutators: {setFieldValue},
    },
  } = useFormRenderer<TConditionForm>();

  const structureCategories = useSelector(selectStructureCategories);
  const auditId = useSelector(selectActiveAuditId);
  const audit = useSelector(selectAudit);
  const [moveInspectionData] = useMoveInspectionDataMutation();

  const [targetCategoryId, setTargetCategoryId] = useState<string>(category.id);

  const targetCategories = isEdit
    ? getDamageMoveTargetCategories(structureCategories, category.id)
    : [];
  const canChangePart = targetCategories.length > 1;
  // Clamp the picked target to a part that is still on offer — the structure can reload mid-edit
  // and drop it. Moving to a target that is no longer there would silently write to the wrong
  // part, so fall back to the current part (an in-place edit, no move).
  const resolvedTargetCategory =
    targetCategories.find((target) => target.id === targetCategoryId) ?? category;
  const isPartChanged = resolvedTargetCategory.id !== category.id;

  const [locationPhotoParam, detailPhotoParam] =
    category.paramDefinitions?.relatedActions?.filter(
      (param) => param.type === AuditParamType.PHOTO
    ) ?? [];
  const locationAssets = useSelector(selectAuditAssets(category.id, locationPhotoParam?.id));
  const detailAssets = useSelector(selectAuditAssets(category.id, detailPhotoParam?.id));

  const typeOfDamageParam = getParamFromCategory(category, AuditParamType.SELECTABLE_BUTTON);
  const commentOfDamageParam = getParamFromCategory(category, AuditParamType.ENHANCED_COMMENT);
  const typeFieldName = getFormFieldName(category.id, typeOfDamageParam?.id);
  const commentFieldName = getFormFieldName(category.id, commentOfDamageParam?.id);

  const getDamageValue = (fieldName: string): AuditDamageValueBody[] => {
    const value = getFieldState(fieldName)?.value;

    return isDamageValueArray(value) ? value : [];
  };

  // Reads a damage-array field (type/comment) from the persisted audit store — the source of
  // truth the damage list and body diagram render from. Unlike getDamageValue (Final Form),
  // this resolves correctly for a category whose fields are not registered in the form, e.g.
  // the move target. Reading the target from Final Form there returns [], which would overwrite
  // (delete) the target part's existing damages on move.
  const getStoredDamageValue = (
    categoryId: string,
    paramDefinitionId: string
  ): AuditDamageValueBody[] => {
    const value = getAuditFieldData(categoryId, paramDefinitionId, audit ?? undefined);

    return isDamageValueArray(value) ? value : [];
  };

  const moveDamage = async (targetCategory: AuditCategoryOfStructure): Promise<void> => {
    const targetTypeParam = getParamFromCategory(targetCategory, AuditParamType.SELECTABLE_BUTTON);
    const targetCommentParam = getParamFromCategory(
      targetCategory,
      AuditParamType.ENHANCED_COMMENT
    );
    const [targetLocationPhotoParam, targetDetailPhotoParam] =
      targetCategory.paramDefinitions?.relatedActions?.filter(
        (param) => param.type === AuditParamType.PHOTO
      ) ?? [];

    if (isNil(auditId) || isNil(damageType) || isNil(typeOfDamageParam) || isNil(targetTypeParam)) {
      return;
    }

    const currentTypeValue = getDamageValue(typeFieldName);
    const damageOrder =
      currentTypeValue.find((damage) => damage.damageId === damageId)?.order ?? lastOrderNumber + 1;

    const getDamageAssetsForParam = (
      paramDefinitionId: string | undefined,
      assets: AuditDataAssetsFilesBody[]
    ): AuditDataAssetsFilesBody[] => {
      if (isNil(paramDefinitionId)) {
        return [];
      }

      return collectDamageAssets({
        damageId,
        assets,
        stagedAssets: assetsForSave[paramDefinitionId] ?? [],
        deletedImageIds: assetsForDelete[paramDefinitionId] ?? [],
      });
    };

    const {requestBody, fieldUpdates, assetRelocations} = buildMoveAuditDamagePayload({
      damageId,
      type: damageType,
      comment: damageComment,
      order: damageOrder,
      source: {
        categoryId: category.id,
        typeParamId: typeOfDamageParam.id,
        commentParamId: commentOfDamageParam?.id,
        typeValue: currentTypeValue,
        commentValue: getDamageValue(commentFieldName),
        photoParams: [
          {
            paramDefinitionId: locationPhotoParam?.id ?? '',
            assets: getDamageAssetsForParam(locationPhotoParam?.id, locationAssets),
          },
          {
            paramDefinitionId: detailPhotoParam?.id ?? '',
            assets: getDamageAssetsForParam(detailPhotoParam?.id, detailAssets),
          },
        ],
      },
      target: {
        categoryId: targetCategory.id,
        typeParamId: targetTypeParam.id,
        commentParamId: targetCommentParam?.id,
        typeValue: getStoredDamageValue(targetCategory.id, targetTypeParam.id),
        commentValue: targetCommentParam
          ? getStoredDamageValue(targetCategory.id, targetCommentParam.id)
          : [],
        photoParamIds: [targetLocationPhotoParam?.id ?? '', targetDetailPhotoParam?.id ?? ''],
      },
    });

    await moveInspectionData({auditId, moveInspectionDataRequestBody: requestBody})
      .unwrap()
      .then(async () => {
        // Delete photos the user removed during this edit session — only after the move has
        // succeeded. Deleting them upfront would permanently lose the photos if the move request
        // failed, leaving the damage on the source part with some of its photos already gone.
        for await (const paramDefinition of [locationPhotoParam, detailPhotoParam]) {
          if (!paramDefinition) {
            continue;
          }
          const imageIdsForDelete = assetsForDelete?.[paramDefinition.id];
          await deleteAssetsForParam(paramDefinition.id, imageIdsForDelete);
          deleteAssetsOfVehicleCreate(imageIdsForDelete);
        }

        fieldUpdates.forEach((update) => setFieldValue(update.fieldName, update.value));

        // Keep the local audit store (the source of truth for the damage list and the body
        // diagram) in sync immediately. Autosave only persists on blur/inactivity, so without
        // this the moved damage would keep rendering under the source part until autosave fires.
        dispatch(
          updateAuditFields(
            Object.fromEntries(fieldUpdates.map((update) => [update.fieldName, update.value]))
          )
        );

        assetRelocations.forEach((relocation) => {
          dispatch(
            deleteAssets({
              categoryId: relocation.fromCategoryId,
              paramDefinitionId: relocation.fromParamId,
              imageIds: compact(relocation.assets.map((asset) => asset.imageId)),
            })
          );
          dispatch(
            addAssets({
              categoryId: relocation.toCategoryId,
              paramDefinitionId: relocation.toParamId,
              assets: relocation.assets,
            })
          );
        });

        // The move persists directly (unlike a plain field edit, which relies on the form's
        // autosave to invalidate these tags) — so widgets reading the vehicle's condition
        // summary or overview would otherwise keep showing pre-move data.
        dispatch(vehicleApi.util.invalidateTags([{type: 'Vehicle', id: vehicleId ?? ''}]));
        dispatch(carAuditApi.util.invalidateTags([{type: 'AuditData', id: vehicleId ?? ''}]));

        onClose();
      })
      .catch(handleApiError);
  };

  return {
    targetCategories,
    canChangePart,
    setTargetCategoryId,
    resolvedTargetCategory,
    isPartChanged,
    moveDamage,
  };
};
